import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import { CustomerAddressFormFieldStyles } from "@insite/content-library/Components/CustomerAddressFormFields";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";
import CustomerAddressFormFields from "./CustomerAddressFormFields";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import cloneDeep from "lodash/cloneDeep";
import ADIColors from "@NA/Utils/ADIColors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { connect } from "react-redux";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { AddressModal, ValidateAddressResponseModel } from "@NA/Services/ADIValidateZipCodeService";
import { validateAddress } from "@NA/Services/ADIInvoiceRecapBankAccountService"; // This property is being imported from different service --- P21 Change
import LoadingOverlay from "@NA/MobiusOverrides/LoadingOverlay";
import ADINewAccountAddressSuggestionModalRecap from "@NA/Components/MyAccountRecapPayment/ADINewAccountAddressSuggestionModalRecap"; // This is being used as P21 Change
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay"; // This is being using as P21 Change.
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { GetShipTosApiParameter } from "../Services/CustomersService";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";

interface OwnProps<Address extends BillToModel | ShipToModel> {
    address: Address;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    saveButtonTextOverride?: string;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit: (event: any, address: Address) => void;
    extendedStyles?: CustomerAddressFormStyles;
    showRemoveInFuture?: boolean;
}

interface State<Address extends BillToModel | ShipToModel> {
    address: Address;
    countryLookup?: CountryModel;
    formErrors: FormErrorsState;
    formSubmitAttempted: boolean;
    isSubmitting: boolean;
    removeinfuture: string;
    isAddressDataInvalid: boolean;
    nicknameProperty: string | undefined;
    // The below properties are from P21 Change
    isAddressSuggestionModalOpen: boolean;
    addressApiResult: any;
    isEdit: boolean;
    addressValidationErrorMessage: string[];
    setSuggestionWraningMessage: string;
    isToggled: boolean;
    saveButtonTextOverride: boolean;
}

type FormErrorsState = {
    firstName?: React.ReactNode;
    lastName?: React.ReactNode;
    attention?: React.ReactNode;
    companyName?: React.ReactNode;
    address1?: React.ReactNode;
    address2?: React.ReactNode;
    address3?: React.ReactNode;
    address4?: React.ReactNode;
    country?: React.ReactNode;
    city?: React.ReactNode;
    state?: React.ReactNode;
    postalCode?: React.ReactNode;
    phone?: React.ReactNode;
    email?: React.ReactNode;
    fax?: React.ReactNode;
    nickname?: React.ReactNode;
    removeinfuture?: React.ReactNode;
};

export interface CustomerAddressFormStyles {
    loadingOverlay?: LoadingOverlayProps;
    form?: InjectableCss;
    formFields?: CustomerAddressFormFieldStyles;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    pleaseEnterText?: InjectableCss;
    errorWrapper?: InjectableCss;
    mainWrapperScroll?: InjectableCss;
    mainWrapperScrollTest?: InjectableCss;
}

export const customerAddressFormStyles: CustomerAddressFormStyles = {
    loadingOverlay: {
        css: css`
            & > div:first-child[data-loading="true"] {
                position: fixed;
            }
        `,
    },
    mainWrapperScroll: {
        css: css`
            overflow-y: auto;
            padding-right: 20px;
            max-height: 50vh;
            min-height: inherit;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-right: 15px;
                        `,
                    ],
                    "max",
                );
            }}
            &::-webkit-scrollbar {
                width: 4px;
                overflow-y: auto;
            }
            &::-webkit-scrollbar-track {
                background: ${getColor("common.background")};
                overflow-y: auto;
            }
            &::-webkit-scrollbar-thumb {
                background-color: ${ADIColors.lightGrey};
                border-radius: 7px;
                overflow-y: auto;
            }
        `,
    },
    mainWrapperScrollTest: {
        css: css`
            overflow-y: auto;
            padding-right: 20px;
            max-height: 50vh;
            min-height: inherit;
            &::-webkit-scrollbar {
                width: 4px;
                overflow-y: auto;
            }
            &::-webkit-scrollbar-track {
                background: ${getColor("common.background")};
                overflow-y: auto;
            }
            &::-webkit-scrollbar-thumb {
                background-color: ${ADIColors.lightGrey};
                border-radius: 7px;
                overflow-y: auto;
            }

            label [data-test-selector="customerAddressForm_city-label"] {
                color: blue !important;
            }
        `,
    },
    errorWrapper: {
        css: css`
            > ${TypographyStyle} {
                font-size: 14px;
                padding-bottom: 9px;
            }
        `,
    },
    pleaseEnterText: {
        css: css`
            font-size: 14px;
            padding-bottom: 9px;
        `,
    },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
            > ${ButtonWrapper} {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                width: 50%;
                                min-width: inherit;
                                max-width: inherit;
                            `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            }
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 25px;
            height: auto;
            min-height: 42px !important;
            border-radius: 4px;
            align-items: center;
            justify-content: center;
            padding: 9px 10px;
            margin-bottom: 2px;
            display: flex;
            min-width: 110px;
            border: 1px solid ${getColor("secondary")};
            &:hover {
                background: ${ADIColors.secondaryButtonHoverLightBlue};
            }
        `,
    },
    submitButton: {
        css: css`
            height: auto;
            min-height: 42px !important;
            border-radius: 4px;
            align-items: center;
            justify-content: center;
            padding: 9px 10px;
            margin-bottom: 2px;
            display: flex;
            min-width: 160px;
        `,
    },
};

// Below mapStateToProps is being using by P21 ----- P21 Change
// const mapStateToProps = (state: ApplicationState) => ({
//     countriesDataFromState: getCurrentCountries(state),
// });

const mapStateToProps = (state: CustomApplicationState) => {
    const billToId = state.data.carts.byId?.current?.billToId;
    let shipTosArray;

    if (billToId) {
        const parameter: GetShipTosApiParameter = {
            billToId: billToId,
            expand: ["validation"],
            exclude: ["showAll", "billTo", "oneTime", "createNew"],
        };
        shipTosArray = getShipTosDataView(state, parameter).value;
    }

    return {
        shipTosArray,
        countriesDataFromState: getCurrentCountries(state),
    };
};

// <---- Below code is available in Spire Prod ---->
// const mapStateToProps = (state: CustomApplicationState) => {
//     const billToId = state.data.carts.byId?.current?.billToId;
//     let shipTosArray;

//     if (billToId) {
//         const parameter: GetShipTosApiParameter = {
//             billToId: billToId,
//             expand: ["validation"],
//             exclude: ["showAll", "billTo", "oneTime", "createNew"],
//         };
//         shipTosArray = getShipTosDataView(state, parameter).value;
//     }

//     return {
//         shipTosArray,
//         countriesDataFromState: getCurrentCountries(state),
//     };
// };

const styles = customerAddressFormStyles;
const StyledForm = getStyledWrapper("form");

const getRequiredErrorMessage = (fieldDisplay: AddressFieldDisplayModel) =>
    siteMessage("Field_Required", fieldDisplay.displayName);

class CustomerAddressForm<Address extends BillToModel | ShipToModel> extends React.Component<
    OwnProps<Address> & ReturnType<typeof mapStateToProps>,
    State<Address>
> {
    private styles: CustomerAddressFormStyles;
    isNotAvailableNickName: boolean;
    isValidatedAllFields: boolean;

    constructor(props: OwnProps<Address> & ReturnType<typeof mapStateToProps>) {
        super(props);
        this.isNotAvailableNickName = false;
        this.isValidatedAllFields = false;
        let newAddressData: typeof address;
        let countryLookup: CountryModel | undefined;

        this.styles = mergeToNew(styles, props.extendedStyles);

        const { address } = this.props;

        newAddressData = {
            ...address,
            country: this.props?.countriesDataFromState && this.props.countriesDataFromState[0],
        };
        if (address.country == null) {
            countryLookup = this.props.countries.find(c => c.id === newAddressData.country?.id);
            this.state = {
                address: newAddressData,
                countryLookup,
                formErrors: {},
                formSubmitAttempted: false,
                isSubmitting: false,
                removeinfuture: "doNotSaveOption",
                isAddressDataInvalid: false,
                nicknameProperty: newAddressData?.properties?.nickname,
                addressApiResult: [],
                isEdit: address.address1 !== "" ? true : false,
                isAddressSuggestionModalOpen: false,
                addressValidationErrorMessage: [""],
                setSuggestionWraningMessage:
                    "Please choose whether you want to use the address you provided or the one we suggest.",
                isToggled: false,
                saveButtonTextOverride: true,

                // Initialize state
            };
        } else {
            countryLookup = this.props.countries.find(c => c.id === address.country?.id);
            this.state = {
                address,
                countryLookup,
                formErrors: {},
                formSubmitAttempted: false,
                isSubmitting: false,
                removeinfuture: "doNotSaveOption",
                isAddressDataInvalid: false,
                nicknameProperty: newAddressData?.properties?.nickname,
                isEdit: address.address1 !== "" ? true : false,
                addressApiResult: [],
                isAddressSuggestionModalOpen: false,
                addressValidationErrorMessage: [],
                setSuggestionWraningMessage:
                    "Please choose whether you want to use the address you provided or the one we suggest.",
                isToggled: true,
                saveButtonTextOverride: true,
            };
        }
    }

    handleRadioChange = (value: any) => {
        this.setState({ removeinfuture: value });
    };

    // handleInputChange2: TextFieldProps["onChange"] = event => {
    //     const { target } = event;
    //     const propertyName = target.name;

    //     if (propertyName === "nickname") {
    //         const propertyName1 = target.value;
    //         this.setState(
    //             () => ({
    //                 nicknameProperty: target.value }),
    //             () => {
    //             }
    //           );
    //         let count = 0;
    //         let data = propertyName1.toLowerCase();
    //         if (propertyName1) {
    //             count = 0;
    //             this.props.shipTosArray?.forEach(
    //                 shipTo => {
    //                     if (shipTo.properties["nickname"] != undefined) {
    //                         if (shipTo.properties["nickname"].toLowerCase() == data) {
    //                             count++;
    //                         }
    //                     }
    //                 });

    //         }
    //             if ((count == 0 || propertyName1 == '')) {
    //                 if(!this.isValidatedAllFields){
    //                     this.setState(
    //                         () => ({
    //                             formErrors: {} }),
    //                         () => {
    //                             this.isAvailableNickName = false;
    //                         }
    //                     );
    //                 }
    //             }
    //             if (count > 0) {
    //                 this.setState(
    //                     () => ({
    //                         nicknameProperty: data }),
    //                     () => {
    //                       this.isAvailableNickName = true;
    //                       if(this.state.nicknameProperty != '')
    //                           this.validateForm();
    //                     }
    //                 );
    //             }
    //     }
    // };
    handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;

        // Clear validation errors when any field is changed
        if (this.state.addressValidationErrorMessage.length > 0) {
            this.setState({
                addressValidationErrorMessage: [],
            });
        }

        if (propertyName === "nickname" || propertyName === "postalcode2") {
            const propertyName1 = target.value;
            this.setState({
                address: {
                    ...this.state.address,
                    properties: {
                        ...this.state.address.properties,
                        [propertyName]: target.value,
                    },
                },
                nicknameProperty: target.value,
            });
            if (propertyName === "nickname") {
                let count = 0;
                let data = propertyName1.toLowerCase();
                if (propertyName1) {
                    count = 0;
                    this.props.shipTosArray?.forEach(shipTo => {
                        if (shipTo.properties["nickname"] != undefined) {
                            if (shipTo.properties["nickname"].toLowerCase() == data) {
                                count++;
                            }
                        }
                    });
                }
                if (count == 0 || propertyName1 == "") {
                    if (!this.isValidatedAllFields) {
                        this.setState(
                            () => ({
                                formErrors: {},
                            }),
                            () => {
                                this.isNotAvailableNickName = false;
                            },
                        );
                        this.setState({
                            address: {
                                ...this.state.address,
                                properties: {
                                    ...this.state.address.properties,
                                    [propertyName]: target.value,
                                },
                            },
                        });
                    }
                }
                if (count > 0) {
                    this.setState(
                        () => ({
                            nicknameProperty: data,
                        }),
                        () => {
                            this.isNotAvailableNickName = true;
                            if (this.state.nicknameProperty != "") this.validateForm();
                        },
                    );
                }
            }
        } else {
            this.setState({
                address: {
                    ...this.state.address,
                    [propertyName]: target.value,
                },
            });
        }
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const countryLookup = this.props.countries.find(c => c.id === country?.id);
        this.setState({
            address: {
                ...this.state.address,
                country,
                state: null,
            },
            countryLookup,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) =>
        this.setState({
            address: {
                ...this.state.address,
                state,
            },
        });

    validateZipCityState = async (addressObj: AddressModal) => {
        try {
            const response = await validateAddress({
                city: addressObj.city?.trim(),
                state: addressObj?.stateName || null,
                postalCode: addressObj.postalCode?.trim(),
            });
            return {
                response,
                successful: true,
            };
        } catch (error) {
            return {
                response: undefined,
                successful: false,
            };
        }
    };

    validateForm = () => {
        const fieldDisplay = this.props.addressFieldDisplayCollection;
        const validation = this.props.address.validation!;
        const { countries } = this.props;
        const isPRRegion = window.location.hostname.endsWith(".pr");

        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;

        const formErrors: FormErrorsState = {};
        if (this.isNotAvailableNickName) {
            if (this.state.nicknameProperty) {
                formErrors.nickname = "The Nickname you entered is already in use. Please enter a unique name.";
            }
        } else {
            if (!this.state.nicknameProperty || this.state.nicknameProperty.trim().length <= 0) {
                formErrors.nickname = "field nick name is required";
            }

            if (fieldDisplay.firstName?.isVisible && validation.firstName?.isRequired && firstName.trim().length <= 0) {
                formErrors.firstName = getRequiredErrorMessage(fieldDisplay.firstName);
            }

            if (fieldDisplay.lastName?.isVisible && validation.lastName?.isRequired && lastName.trim().length <= 0) {
                formErrors.lastName = getRequiredErrorMessage(fieldDisplay.lastName);
            }

            if (
                !isPRRegion &&
                fieldDisplay.attention?.isVisible &&
                validation.attention?.isRequired &&
                attention.trim().length <= 0
            ) {
                formErrors.attention = getRequiredErrorMessage(fieldDisplay.attention);
            }

            if (
                fieldDisplay.companyName?.isVisible &&
                validation.companyName?.isRequired &&
                companyName.trim().length <= 0
            ) {
                formErrors.companyName = getRequiredErrorMessage(fieldDisplay.companyName);
            }

            if (fieldDisplay.address1?.isVisible && validation.address1?.isRequired && address1.trim().length <= 0) {
                formErrors.address1 = getRequiredErrorMessage(fieldDisplay.address1);
            }

            if (fieldDisplay.address2?.isVisible && validation.address2?.isRequired && address2.trim().length <= 0) {
                formErrors.address2 = getRequiredErrorMessage(fieldDisplay.address2);
            }

            if (
                !isPRRegion &&
                fieldDisplay.address3?.isVisible &&
                validation.address3?.isRequired &&
                address3.trim().length <= 0
            ) {
                formErrors.address3 = getRequiredErrorMessage(fieldDisplay.address3);
            }

            if (
                !isPRRegion &&
                fieldDisplay.address4?.isVisible &&
                validation.address4?.isRequired &&
                address4.trim().length <= 0
            ) {
                formErrors.address4 = getRequiredErrorMessage(fieldDisplay.address4);
            }

            if (countries.length > 1 && !country && fieldDisplay.country) {
                formErrors.country = getRequiredErrorMessage(fieldDisplay.country);
            }

            if (fieldDisplay.city?.isVisible && validation.city?.isRequired && city.trim().length <= 0) {
                formErrors.city = getRequiredErrorMessage(fieldDisplay.city);
            }

            const { countryLookup } = this.state;

            if (
                countries.length > 0 &&
                countryLookup?.states &&
                countryLookup.states.length > 0 &&
                !state &&
                fieldDisplay.state
            ) {
                formErrors.state = getRequiredErrorMessage(fieldDisplay.state);
            }

            if (
                fieldDisplay.postalCode?.isVisible &&
                validation.postalCode?.isRequired &&
                postalCode.trim().length <= 0
            ) {
                formErrors.postalCode = getRequiredErrorMessage(fieldDisplay.postalCode);
            }

            if (
                !isPRRegion &&
                fieldDisplay.phone?.isVisible &&
                validation.phone?.isRequired &&
                phone.trim().length <= 0
            ) {
                formErrors.phone = getRequiredErrorMessage(fieldDisplay.phone);
            } else if (
                !isPRRegion &&
                phone.trim().length > 0 &&
                !/^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/g.test(phone)
            ) {
                formErrors.phone = siteMessage("AddressInfo_Phone_Validation");
            }

            if (
                !isPRRegion &&
                fieldDisplay.email?.isVisible &&
                validation.email?.isRequired &&
                email.trim().length <= 0
            ) {
                formErrors.email = getRequiredErrorMessage(fieldDisplay.email);
            } else if (
                !isPRRegion &&
                email.trim().length > 0 &&
                !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(email)
            ) {
                formErrors.email = siteMessage("AddressInfo_EmailAddress_Validation");
            }

            if (!isPRRegion && fieldDisplay.fax?.isVisible && validation.fax?.isRequired && fax.trim().length <= 0) {
                formErrors.fax = getRequiredErrorMessage(fieldDisplay.fax);
            } else if (
                !isPRRegion &&
                fax.trim().length > 0 &&
                !/^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/g.test(fax)
            ) {
                formErrors.fax = siteMessage("AddressInfo_Phone_Validation");
            }
        }

        this.setState({ formErrors });

        return formErrors;
    };

    compareAddresses = (originalAddress: any, propsedAddress: any) => {
        // Check if all relevant fields are the same
        return (
            (originalAddress.address1 || "").toUpperCase() === (propsedAddress.address1 || "").toUpperCase() &&
            (originalAddress.address2 || "").toUpperCase() === (propsedAddress.address2 || "").toUpperCase() &&
            (originalAddress.address3 === "" || originalAddress.address3 === null || (originalAddress.address3 || "").toUpperCase() === (propsedAddress.address3 || "").toUpperCase()) &&
            (originalAddress.city || "").toUpperCase() === (propsedAddress.city || "").toUpperCase() &&
            (originalAddress.state?.abbreviation || "").toUpperCase() === (propsedAddress.stateProv || "").toUpperCase() &&
            originalAddress.postalCode === propsedAddress.postalCode &&
            (originalAddress.country?.abbreviation || "").toUpperCase() === (propsedAddress.country || "").toUpperCase()
        );
    };

    handleValidateAddress = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState(
            {
                isSubmitting: true,
                addressValidationErrorMessage: [],
                formErrors: {},
            },
            async () => {
                const isPRRegion = window.location.hostname.endsWith(".pr");

                const formErrors = this.validateForm();

                if (isPRRegion) {
                    delete formErrors.attention;
                    delete formErrors.address3;
                    delete formErrors.address4;
                    delete formErrors.phone;
                    delete formErrors.fax;
                    delete formErrors.email;
                }

                if (Object.keys(formErrors).length > 0) {
                    this.setState({ isSubmitting: false });
                    return;
                }

                const { state, city, postalCode, address1, address2, companyName } = this.state.address;
                const payload = {
                    address1: address1,
                    address2: address2,
                    city: city,
                    companyName: companyName,
                    country: this.props?.countriesDataFromState && this.props.countriesDataFromState[0],
                    postalCode: postalCode,
                    state: { abbreviation: state?.abbreviation },
                };

                const commonValidationErrors = [
                    translate("Street Not Validated"),
                    translate("City/State Not Validated"),
                    translate("Postal Code Not Validated"),
                    translate("Street Address Not Validated"),
                ];

                if (
                    address1 !== this.props.address.address1 ||
                    address2 !== this.props.address.address2 ||
                    city !== this.props.address.city ||
                    companyName !== this.props.address.companyName ||
                    postalCode !== this.props.address.postalCode ||
                    state?.abbreviation !== this.props.address.state?.abbreviation ||
                    this.state.isEdit
                ) {
                    const result = await validateAddress(payload);

                    this.setState({ isSubmitting: false });

                    if (result.successful) {
                        const hasValidationMessage =
                            result.result?.validationMessage && result.result.validationMessage.trim() !== "";

                        const validationScore = result.result?.proposedAddress?.validationScore || 0;
                        const isValidated = result.result?.proposedAddress?.validated === "Y";

                        if (validationScore >= 80 && isValidated && !hasValidationMessage) {
                            const comparisionResult = this.compareAddresses(
                                result.result?.originalAddress,
                                result.result?.proposedAddress,
                            );

                            if (comparisionResult) {
                                this.submitHandler("provided");
                            } else {
                                // Show suggestion modal
                                this.setState({
                                    addressApiResult: result.result,
                                    isAddressSuggestionModalOpen: true,
                                    setSuggestionWraningMessage: translate(
                                        "Please choose whether you want to use the address you provided or the one we suggest",
                                    ),
                                });

                                const element = document.getElementById("new-address-modal");
                                if (element) {
                                    element.style.display = "none";
                                }
                            }
                        } else if (hasValidationMessage) {
                            const validationError = result.result.validationMessage
                                .split(";")
                                .map((msg: string) => msg.trim())
                                .filter((msg: string) => msg !== "");

                            const isOtherError = validationError.every((element: string) =>
                                commonValidationErrors.some(err => element.includes(err)),
                            );

                            if (!isOtherError) {
                                this.setState({
                                    addressValidationErrorMessage: [
                                        translate(
                                            "We couldn’t validate one or more address fields. Please ensure your address is complete and accurate.*",
                                        ),
                                    ],
                                });
                            } else {
                                const updatedErrors: FormErrorsState = { ...this.state.formErrors };
                                validationError.forEach((message: string) => {
                                    let fieldName = "";
                                    let errorMessage = "";

                                    if (message.toLowerCase().includes("street")) {
                                        fieldName = "address1";
                                        errorMessage = "Street address is not valid";
                                    } else if (message.toLowerCase().includes("city/state")) {
                                        fieldName = "city";
                                        errorMessage = "City/State information is not valid";
                                    } else if (message.toLowerCase().includes("postal")) {
                                        fieldName = "postalCode";
                                        errorMessage = "Postal code is not valid";
                                    }

                                    if (fieldName && errorMessage) {
                                        updatedErrors[fieldName as keyof FormErrorsState] = errorMessage;
                                    }
                                });

                                this.setState({
                                    formErrors: updatedErrors,
                                    addressValidationErrorMessage: validationError,
                                });
                            }
                        } else {
                            // Show suggestion modal
                            this.setState({
                                addressApiResult: result.result,
                                isAddressSuggestionModalOpen: true,
                                setSuggestionWraningMessage: translate(
                                    "Please choose whether you want to use the address you provided or the one we suggest",
                                ),
                            });
                        }
                    }
                } else {
                    // No changes to address, just submit
                    this.props.onSubmit(event, { ...this.state.address });
                }
            },
        );
    };

    submitHandler = async (selectedAddress: string, comparisionResult?: boolean) => {
        const finalAddress =
            selectedAddress.toLowerCase() === "provided"
                ? this.state.address
                : this.state.addressApiResult?.proposedAddress;
        this.setState({ formSubmitAttempted: true, isSubmitting: true });

        // if (this.state.removeinfuture === "doNotSaveOption") {
        //     finalAddress.properties.removeinfuture = "Y";
        // }

        const formErrors = this.validateForm();

        const isPRRegion = window.location.hostname.endsWith(".pr");
        if (isPRRegion) {
            delete formErrors.attention;
            delete formErrors.address3;
            delete formErrors.address4;
            delete formErrors.phone;
            delete formErrors.fax;
            delete formErrors.email;
        }

        const { state, city, postalCode } = this.state.address;

        const stateName = selectedAddress.toLowerCase() === "provided" ? state?.abbreviation : finalAddress.stateProv;

        const addressObj: AddressModal = { postalCode, city, stateName };
        const correctState = this.state.address.country?.states?.find(
            s => s.abbreviation === stateName
        ) || {
            id: null,
            name: null,
            abbreviation: stateName
        };

        this.setState({ formErrors }, () => {
            if (Object.keys(this.state.formErrors).length === 0) {
                this.props.onSubmit(event, {
                    ...this.state.address,
                    ...finalAddress,
                    state: correctState, // for handling state object in address provide selection
                    country: this.props?.countriesDataFromState && this.props.countriesDataFromState[0],
                });
            } else {
                this.setState({
                    isSubmitting: false,
                    isAddressDataInvalid: false,
                });
            }
        });
    };

    handleSuggestionSelection = (selectedAddress: string) => {
        console.log(selectedAddress, 'address');

        this.submitHandler(selectedAddress);
    };

    message = {
        nickname: "Nickname",
        firstName: "First Name",
        lastName: "Last Name",
        companyName: "Company",
        address1: "Address Line 1",
        city: "City",
        state: "State",
        postalCode: "Zip Code",
    };

    getMessage = (key: "nickname" | "firstName") => {
        return this.message[key] ? this.message[key] : key;
    };

    Closemodel = () => {
        this.setState(prevState => ({
            isAddressSuggestionModalOpen: false,
            isEdit: true,
            isToggled: true,
            saveButtonTextOverride: false,
        }));
        const element = document.getElementById("new-address-modal");
        if (element) {
            element.style.display = "flex";
        }
    };

    render() {
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;
        const { formErrors } = this.state;
        const { countries, extendedStyles } = this.props;
        const { validation } = this.props.address;
        const fieldDisplay = this.props.addressFieldDisplayCollection;

        if (!validation) {
            return null;
        }
        const isSaveDisabled =
            (validation.firstName!.isDisabled &&
                validation.lastName!.isDisabled &&
                validation.companyName!.isDisabled &&
                validation.attention!.isDisabled &&
                validation.address1!.isDisabled &&
                validation.address2!.isDisabled &&
                validation.address3!.isDisabled &&
                validation.address4!.isDisabled &&
                validation.country!.isDisabled &&
                validation.city!.isDisabled &&
                validation.postalCode!.isDisabled &&
                validation.phone!.isDisabled &&
                validation.fax!.isDisabled &&
                validation.email!.isDisabled) ||
            this.state.isSubmitting;

        return (
            <LoadingOverlay {...styles.loadingOverlay} loading={this.state.isSubmitting}>
                <StyledForm {...styles.form} onSubmit={this.handleValidateAddress} noValidate>
                    {Object.keys(formErrors).length > 0 &&
                        (!this.state.addressValidationErrorMessage.length ||
                            !this.state.addressValidationErrorMessage.some(error => error.trim() !== "")) ? (
                        this.isNotAvailableNickName ? (
                            <>
                                <StyledWrapper>
                                    <Typography as="p" {...styles.pleaseEnterText}>
                                        * Please enter information in required fields.
                                    </Typography>
                                </StyledWrapper>
                                <Typography as="p" color="danger">
                                    {Object.values(formErrors).map((item: any) => (
                                        <Typography as="span">{item}</Typography>
                                    ))}
                                </Typography>
                            </>
                        ) : (
                            <StyledWrapper {...styles.errorWrapper}>
                                <Typography as="p" color="danger">
                                    * Please enter information in required fields.
                                </Typography>
                                <Typography as="p" color="danger">
                                    *{" "}
                                    {Object.keys(formErrors).map((item: any) => (
                                        <Typography as="span">{this.getMessage(item)}, </Typography>
                                    ))}{" "}
                                    has invalid characters.
                                </Typography>
                                <Typography as="p" color="danger">
                                    * [Alphabet, Numbers, Space, Comma, ., *, -, \\, #, / and & are only allowed]
                                </Typography>
                            </StyledWrapper>
                        )
                    ) : (
                        <StyledWrapper>
                            <Typography as="p" {...styles.pleaseEnterText}>
                                * Please enter information in required fields.
                            </Typography>
                        </StyledWrapper>
                    )}
                    {this.state.addressValidationErrorMessage &&
                        this.state.addressValidationErrorMessage.map((error: string) => {
                            return (
                                error && (
                                    <Typography color="danger" size={14}>
                                        {translate(`* ${error}`)}
                                        <br />
                                    </Typography>
                                )
                            );
                        })}
                    {this.state.isAddressDataInvalid &&
                        state?.name.trim().length !== 0 &&
                        city.trim().length !== 0 &&
                        postalCode.trim().length !== 0 && (
                            <Typography as="p" color="danger">
                                * City, State and Zip combination does not match."
                            </Typography>
                        )}
                    <StyledWrapper {...styles.mainWrapperScrollTest}>
                        <CustomerAddressFormFields
                            firstName={firstName}
                            lastName={lastName}
                            attention={attention}
                            companyName={companyName}
                            address1={address1}
                            address2={address2}
                            address3={address3}
                            address4={address4}
                            country={country}
                            city={city}
                            state={state}
                            postalCode={postalCode}
                            phone={phone}
                            email={email}
                            fax={fax}
                            nickname={this.state.nicknameProperty}
                            nickNameError={formErrors.nickname}
                            onChangeNickName={this.handleInputChange}
                            postalcode2={this.state.address.properties.postalcode2}
                            removeinfuture={this.state.removeinfuture}
                            onChangeRemoveInFuture={this.handleRadioChange}
                            onChangePostalCode2={this.handleInputChange}
                            onChangeFirstName={this.handleInputChange}
                            onChangeLastName={this.handleInputChange}
                            onChangeAttention={this.handleInputChange}
                            onChangeCompanyName={this.handleInputChange}
                            onChangeAddress1={this.handleInputChange}
                            onChangeAddress2={this.handleInputChange}
                            onChangeAddress3={this.handleInputChange}
                            onChangeAddress4={this.handleInputChange}
                            onChangeCountry={this.handleCountryChange}
                            onChangeState={this.handleStateChange}
                            onChangeCity={this.handleInputChange}
                            onChangePostalCode={this.handleInputChange}
                            onChangePhone={this.handleInputChange}
                            onChangeEmail={this.handleInputChange}
                            onChangeFax={this.handleInputChange}
                            firstNameError={formErrors.firstName}
                            lastNameError={formErrors.lastName}
                            attentionError={formErrors.attention}
                            companyNameError={formErrors.companyName}
                            address1Error={formErrors.address1}
                            address2Error={formErrors.address2}
                            address3Error={formErrors.address3}
                            address4Error={formErrors.address4}
                            countryError={formErrors.country}
                            stateError={formErrors.state}
                            cityError={formErrors.city}
                            postalCodeError={formErrors.postalCode}
                            phoneError={formErrors.phone}
                            emailError={formErrors.email}
                            faxError={formErrors.fax}
                            countries={countries}
                            validation={validation}
                            fieldDisplay={fieldDisplay}
                            extendedStyles={extendedStyles?.formFields}
                            showRemoveInFuture={this.props.showRemoveInFuture}
                        />
                        <StyledWrapper {...styles.formButtonsWrapper}>
                            {this.props.onCancel && this.state.isToggled && (
                                <Button
                                    {...this.styles.cancelButton}
                                    type="button"
                                    onClick={this.props.onCancel}
                                    data-test-selector="customerAddressForm_cancel"
                                >
                                    {translate("Cancel")}
                                </Button>
                            )}
                            <Button
                                {...this.styles.submitButton}
                                type="submit"
                                disabled={isSaveDisabled}
                                data-test-selector="customerAddressForm_save"
                            >
                                {this.state.saveButtonTextOverride ? translate("Continue") : translate("Save")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledForm>
                <ADINewAccountAddressSuggestionModalRecap
                    openModal={this.state.isAddressSuggestionModalOpen}
                    closeModal={this.Closemodel}
                    address={this.state.addressApiResult}
                    handleSuggestionSelection={this.handleSuggestionSelection}
                    title={this.state.isEdit ? "Edit Address" : "New Address"}
                    isEdit={this.state.isEdit}
                    nickName={this.state.nicknameProperty}
                    warningMessage={this.state.setSuggestionWraningMessage}
                />
            </LoadingOverlay>
        );
    }
}

export default connect(mapStateToProps)(CustomerAddressForm);
